import { useState, useEffect, useContext, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import {
  Badge,
  Card,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
} from "@mui/material";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import { Link } from "react-router-dom";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";
import ContactAvatar from "components/UpStyled/ContactAvatar";
import NumberFormat from "react-number-format";
import curved9 from "assets/images/upstyled/default_background.png";
import { AccountContext } from "context/account";
import moment from "moment";
import { canResizeImageUrl, getStyleImages, getFileImage } from "utils/imageHelper";
import ProjectStatusTag from "components/UpStyled/ProjectStatusTag";
import Prompt from "components/UpStyled/Prompt";
import SuiAvatar from "components/SuiAvatar";
import DeleteIcon from "@mui/icons-material/Delete";
import ClipLoader from "react-spinners/ClipLoader";
import api from "services/api";
import toast from "react-hot-toast";
import { backendServer } from "services/request";
import socketIOClient from "socket.io-client";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

function ProjectCard({
  project = { services: [] },
  product = null,
  className,
  detailUrl = "#",
  actions,
}) {
  const [images, setImages] = useState([]);
  const metadata = project?.metadata ? project.metadata : {}; //project metadata
  const products = project?.products || [];

  const account = useContext(AccountContext);
  let userid = account.id;

  useEffect(() => {
    if (product) {
      let imageFiles = product?.style?.imageFiles || [];
      let techPacksFiles = product?.style?.techPacksFiles || [];
      let temp = [...imageFiles, ...techPacksFiles];

      if (account.account_type == "de") {
        temp = getStyleImages(product, true);
      }
      setImages(temp);
    } else {
      let styleImages = [];
      //add style first image
      for (let i = 0; i < products.length; i++) {
        let imageFiles = products[i]?.style?.imageFiles || [];
        let techPacksFiles = products[i]?.style?.techPacksFiles || [];

        let temp = [...imageFiles, ...techPacksFiles];
        if (account.account_type == "de") {
          temp = getStyleImages(products[i], false);
        }
        styleImages.push(...temp);
      }
      //add all project reference image
      const fileList = metadata["othersFiles"] || [];
      const files = fileList.filter((f) => f?.type && f.type.includes("image"));
      if (files && files.length > 0) {
        styleImages.push(...files);
      }
      if (styleImages.length == 0) {
        styleImages = [{ url: curved9, name: "deafult_image" }];
      }
      setImages(styleImages);
    }
  }, [products, metadata, product]);

  const servicesDetail = project?.quote?.services_required || {};
  const serviceRequired = Object.keys(servicesDetail);

  const formatProjectDate = (project) => {
    let prefix = "";
    let date = "";
    if (project.status == "active") {
      prefix = "Project was desired end at: ";
      date = project?.quote?.desired_end_date
        ? moment(project.quote.desired_end_date).format("yyyy-MM-DD")
        : "No Desired Date";
    } else if (project.status == "requested" || project.status == "init") {
      prefix = "Project was desired start at: ";
      date = project?.metadata?.desired_start_date
        ? moment(project.metadata.desired_start_date).format("yyyy-MM-DD")
        : "No Desired Date";
    } else if (project.status == "completed") {
      prefix = "Project finished at: ";
      date = project?.metadata?.completed_at
        ? moment(project.metadata.completed_at).format("yyyy-MM-DD")
        : "No Date";
    } else if (project.status == "canceled") {
      prefix = "Project canceled at: ";
      date = project?.metadata?.canceled_at
        ? moment(project.metadata.canceled_at).format("yyyy-MM-DD")
        : "No Date";
    }
    if (prefix) {
      return (
        <SuiTypography
          variant="body1"
          textTransform="capitalize"
          color="text"
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical",
          }}
        >
          {prefix} {date}
        </SuiTypography>
      );
    } else {
      return (
        <SuiTypography
          variant="body1"
          textTransform="capitalize"
          color="text"
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical",
          }}
        >
          N/A
        </SuiTypography>
      );
    }
  };

  const formatProjectExpectDate = (project) => {
    const endDate = project?.quote?.desired_end_date
      ? moment(project.quote.desired_end_date).format("MM/DD/yyyy")
      : "N/A";

    const startDate = project?.metadata?.desired_start_date
      ? moment(project.metadata.desired_start_date).format("MM/DD/yyyy")
      : "N/A";

    return (
      <SuiBox>
        <SuiTypography variant="body1" textTransform="capitalize" color="text">
          {`Project was desired start at: ${startDate}`}
        </SuiTypography>
        <SuiTypography variant="body1" textTransform="capitalize" color="text">
          {`Project was desired end at: ${endDate}`}
        </SuiTypography>
      </SuiBox>
    );
  };

  const budgetMin = project?.quote?.budget_min;
  const budgetMax = project?.quote?.budget_max;

  return (
    <Card
      sx={{
        borderColor: ({ borders }) => borders.borderColor,
        boxShadow: ({ boxShadows: { md } }) => md,
        width: "100%",
      }}
    >
      <SuiBox display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
        <SuiBox position="relative" width="100.25%" sx={{ maxWidth: 180 }}>
          <Link to={detailUrl}>
            <SuiBox position="relative" width="100.25%">
              <ImageCardWrapper
                data={images && images.length > 0 ? images[0] : ""}
                height={200}
                width={180}
                sx={{ overflow: "hidden" }}
              />
            </SuiBox>
          </Link>
        </SuiBox>
        <SuiBox position="relative" width="100%" sx={{ minWidth: 280, pr: 1 }}>
          <SuiBox py={1} pl={1} pr={4} sx={{ width: "100%" }}>
            <SuiBox display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
              <SuiBox>
                <Tooltip title={project.name}>
                  <SuiBox>
                    <Link to={detailUrl}>
                      <SuiTypography
                        variant="h6"
                        fontWeight="medium"
                        textTransform="capitalize"
                        lineHeight="1.2"
                        sx={{
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {project.name}
                      </SuiTypography>
                    </Link>
                  </SuiBox>
                </Tooltip>
              </SuiBox>

              <SuiBox>
                <ProjectStatusTag status={project.status} />
              </SuiBox>
            </SuiBox>
            <SuiTypography
              variant="body1"
              textTransform="none"
              color="text"
              lineHeight="1.2"
              sx={{
                width: "100%",
                minHeight: "32px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
              }}
            >
              {project.description
                ? project.description
                : "There is no description for this project!"}
            </SuiTypography>
            {account.account_type == "ma" ? (
              project.status == "requested" ? (
                formatProjectExpectDate(project)
              ) : (
                formatProjectDate(project)
              )
            ) : (
              <div>
                <div style={{ minHeight: 25, maxWidth: "100%" }}>
                  <SuiBox display="flex" sx={{ flexWrap: "wrap" }}>
                    {serviceRequired.map((s, index) => (
                      <SuiBadge
                        key={index}
                        variant="contained"
                        color="secondary"
                        badgeContent={s}
                        size="sm"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </SuiBox>
                </div>

                <SuiBox display="flex" sx={{ flexWrap: "wrap" }}>
                  <SuiBox key={"budget"} display="flex" py={0.5} pr={2} alignItems="center">
                    <SuiTypography
                      variant="body1"
                      fontWeight="bold"
                      textTransform="capitalize"
                      mr={0.5}
                    >
                      Budget:
                    </SuiTypography>
                    {budgetMin != budgetMax ? (
                      <SuiBox display="flex" alignItems="center">
                        <NumberFormat
                          value={budgetMin}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                          renderText={(value, props) => (
                            <SuiTypography
                              variant="body1"
                              fontWeight="regular"
                              color="text"
                              {...props}
                            >
                              {value}
                            </SuiTypography>
                          )}
                        />
                        ~{" "}
                        {budgetMax > 0 ? (
                          <NumberFormat
                            value={budgetMax}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"$"}
                            renderText={(value, props) => (
                              <SuiTypography
                                variant="body1"
                                fontWeight="regular"
                                color="text"
                                {...props}
                              >
                                {value}
                              </SuiTypography>
                            )}
                          />
                        ) : null}
                      </SuiBox>
                    ) : (
                      <NumberFormat
                        value={budgetMin}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"$"}
                        renderText={(value, props) => (
                          <SuiTypography
                            variant="body1"
                            fontWeight="regular"
                            color="text"
                            {...props}
                          >
                            {value}
                          </SuiTypography>
                        )}
                      />
                    )}
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" sx={{ flexWrap: "wrap" }}>
                  <SuiBox key={"budget"} display="flex" py={0.5} pr={2} alignItems="center">
                    <SuiTypography
                      variant="body1"
                      fontWeight="bold"
                      textTransform="capitalize"
                      mr={0.5}
                    >
                      Number of Styles:
                    </SuiTypography>

                    <SuiTypography
                      variant="body1"
                      textTransform="none"
                      color="text"
                      lineHeight="1.2"
                    >
                      {project?.products ? project?.products.length : 0}
                    </SuiTypography>
                  </SuiBox>
                </SuiBox>
              </div>
            )}
          </SuiBox>
        </SuiBox>
      </SuiBox>
    </Card>
  );
}

export default ProjectCard;
